import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { List, Box, ListItemText, Collapse } from '@mui/material';
import { ExitToApp as ExitToAppIcon } from '@mui/icons-material';
import SidebarLogo from '../../assets/main/sidebar-logo.png'
import DashboardLogo from '../../assets/sidebar/dashboard-icon.svg'
import InvoiceLogo from '../../assets/sidebar/invoice-listing.svg'
import ShiftLogo from '../../assets/sidebar/shift-monitoring.svg'
import ReportLogo from '../../assets/sidebar/report-icon.svg'
import SettingsLogo from '../../assets/sidebar/settings.svg'
import { useSidebar } from '../../context/SidebarContext';
import SidebarBg from "../../assets/facility/sidebarbg.svg";
import ManagementLogo from '../../assets/sidebar/management-icon.svg'
import { useSelector } from 'react-redux';
import { selectFacilityProfile } from '../../modules/facilityProfile/selectors';

import {
  ChildItem,
  LogoContainer,
  LogoText,
  OrgText,
  StyledDrawer,
  StyledListItemButton,
  StyledListItemIcon,
} from './styles';

interface FacilitySidebarProps {
  onLogout: () => void;
}




const FacilitySidebar: React.FC<FacilitySidebarProps> = ({ onLogout }) => {
  const { activePage } = useSidebar();
  const facilityProfile = useSelector(selectFacilityProfile);
  const [isManagementSubMenuOpen, setManagementSubMenuOpen] = useState(false);

  const isManagementActive = activePage === 'management' || activePage === 'new_shift' || activePage === 'manage_shifts' || activePage === 'facility-shift-requests';

  const handleManagementClick = () => {
    setManagementSubMenuOpen(!isManagementSubMenuOpen);
  };

  React.useEffect(() => {
    if (!isManagementActive) {
      setManagementSubMenuOpen(false);
    }
  }, [isManagementActive]);

  return (
    <>
      {/* 1. FIXED LOGO CONTAINER */}
      <LogoContainer>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <img src={SidebarLogo} alt="Logo" style={{ height: '32px', width: '32px' }} />
          <Box sx={{ ml: 2, display: 'flex', flexDirection: 'column' }}>
            <LogoText>Talent Hub</LogoText>
            <OrgText variant="caption">{facilityProfile?.facility_name || 'Facility'}</OrgText>
          </Box>
        </Box>
      </LogoContainer>

      {/* 2. NAVIGATION DRAWER (Starts below logo) */}
      <StyledDrawer variant="permanent">
        <List>
          {/* Dashboard */}
          <Link to="/facility-dashboard" style={{ textDecoration: 'none', color: 'inherit' }}>
            <StyledListItemButton selected={activePage === 'dashboard'}>
              <StyledListItemIcon>
                <img src={DashboardLogo} alt="Dashboard" style={{ height: '16px', width: '16px' }} />
              </StyledListItemIcon>
              <ListItemText primary="Dashboard" />
            </StyledListItemButton>
          </Link>

          {/* Management (Parent Item) */}
          <StyledListItemButton selected={isManagementActive} onClick={handleManagementClick}>
            <StyledListItemIcon>
              <img src={ManagementLogo} alt="Management" style={{ height: '16px', width: '16px' }} />
            </StyledListItemIcon>
            <ListItemText primary="Shift Management" />
          </StyledListItemButton>

          {/* Management Sub-Items */}
          <Collapse in={isManagementSubMenuOpen || isManagementActive} timeout="auto" unmountOnExit>
            <List component="div" disablePadding>

              {/* New Shift (Inactive Child Item) */}
              <Link to="/facility/shift/create/" style={{ textDecoration: 'none', color: 'inherit' }}>
                <ChildItem selected={activePage === 'new_shift'}>
                  <Box sx={{ width: '6px', height: '6px', bgcolor: 'text.primary', borderRadius: '2px', mr: 2 }} />
                  <ListItemText primary="Add New Shift" />
                </ChildItem>
              </Link>

              <Link to="/facility/shifts/" style={{ textDecoration: 'none', color: 'inherit' }}>
                <ChildItem selected={activePage === 'manage_shifts'}>
                  <Box sx={{ width: '6px', height: '6px', bgcolor: 'text.primary', borderRadius: '2px', mr: 2 }} />
                  <ListItemText primary="Manage Shifts" />
                </ChildItem>
              </Link>

              <Link to="/facility-shift-requests" style={{ textDecoration: 'none', color: 'inherit' }}>
                <ChildItem selected={activePage === 'facility-shift-requests'}>
                  <Box sx={{ width: '6px', height: '6px', bgcolor: 'text.primary', borderRadius: '2px', mr: 2 }} />
                  <ListItemText primary="Shift Requests" />
                </ChildItem>
              </Link>

            </List>
          </Collapse>


          {/* Other Items */}
          <Link to="/facility/shifts/ongoing" style={{ textDecoration: 'none', color: 'inherit' }}>
            <StyledListItemButton selected={activePage === 'shift_monitoring'}>
              <StyledListItemIcon>
                <img src={ShiftLogo} alt="Shift Monitoring" style={{ height: '16px', width: '16px' }} />
              </StyledListItemIcon>
              <ListItemText primary="Shift Monitoring" />
            </StyledListItemButton>
          </Link>

          <Link to="/facility/reports" style={{ textDecoration: 'none', color: 'inherit' }}>
            <StyledListItemButton selected={activePage === 'facility_report'}>
              <StyledListItemIcon>
                <img src={ReportLogo} alt="Report-analytics" style={{ height: '16px', width: '16px' }} />
              </StyledListItemIcon>
              <ListItemText primary="Reports" />
            </StyledListItemButton>
          </Link>


          <Link to="/facility/invoices" style={{ textDecoration: 'none', color: 'inherit' }}>
            <StyledListItemButton selected={activePage === 'invoices'}>
              <StyledListItemIcon>
                <img src={InvoiceLogo} alt="Invoices" style={{ height: '16px', width: '16px' }} />
              </StyledListItemIcon>
              <ListItemText primary="Invoices" />
            </StyledListItemButton>
          </Link>

          <Link to="/facility/profile/" style={{ textDecoration: 'none', color: 'inherit' }}>
            <StyledListItemButton selected={activePage === 'profile'}>
              <StyledListItemIcon>
                <img src={SettingsLogo} alt="Settings" style={{ height: '16px', width: '16px' }} />
              </StyledListItemIcon>
              <ListItemText primary="Settings" />
            </StyledListItemButton>
          </Link>
        </List >
        <Box
          sx={{
            width: "100%",
            height: 249,
            backgroundImage: `url(${SidebarBg})`,
            backgroundRepeat: "no-repeat",
            backgroundPosition: "bottom center",
            backgroundSize: "cover",
            pointerEvents: "none",
            position: "absolute",
            bottom: 0,
            zIndex: -1,
            left: 0
          }}
        />

        {/* Optional: Logout at the bottom */}
        <List sx={{ mt: 'auto' }}>
          <StyledListItemButton onClick={onLogout}>
            <StyledListItemIcon> <ExitToAppIcon /> </StyledListItemIcon>
            <ListItemText primary="Logout" />
          </StyledListItemButton>
        </List>
      </StyledDrawer >
    </>
  );
};

export default FacilitySidebar;